import { createContext, useContext, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";

export const THEMES = {
  navy: {
    id: "navy",
    name: "Navy",
    colors: {
      primary: "#0D1B36",
      primaryLight: "#1E3A5F",
      primaryDark: "#0A0F1E",
      secondary: "#E8690A",
      secondaryLight: "#F59E0B",
      accent: "#4F46E5",
      accentLight: "#EDE7F6",
      success: "#1A7A4A",
      warning: "#F59E0B",
      error: "#C0392B",
      background: "#F4F6FB",
      surface: "#FFFFFF",
      surfaceAlt: "#E4E7EF",
      text: "#0D1B36",
      textMuted: "#5A6278",
      textInverse: "#FFFFFF",
      border: "#DDE1E9",
    },
    gradients: {
      primary: "linear-gradient(135deg, #0D1B36 0%, #1E3A5F 100%)",
      secondary: "linear-gradient(135deg, #E8690A 0%, #F59E0B 100%)",
      accent: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
    },
    shadows: {
      card: "0 2px 8px rgba(13, 27, 54, 0.06)",
      cardHover: "0 8px 24px rgba(13, 27, 54, 0.12)",
      glow: "0 4px 16px rgba(79, 70, 229, 0.3)",
      glowSecondary: "0 4px 16px rgba(232, 105, 10, 0.25)",
    },
  },
  ocean: {
    id: "ocean",
    name: "Ocean",
    colors: {
      primary: "#0C4A6E",
      primaryLight: "#075985",
      primaryDark: "#082F49",
      secondary: "#06B6D4",
      secondaryLight: "#67E8F9",
      accent: "#3B82F6",
      accentLight: "#DBEAFE",
      success: "#10B981",
      warning: "#F59E0B",
      error: "#EF4444",
      background: "#F0F9FF",
      surface: "#FFFFFF",
      surfaceAlt: "#E0F2FE",
      text: "#0C4A6E",
      textMuted: "#64748B",
      textInverse: "#FFFFFF",
      border: "#BAE6FD",
    },
    gradients: {
      primary: "linear-gradient(135deg, #0C4A6E 0%, #075985 100%)",
      secondary: "linear-gradient(135deg, #06B6D4 0%, #67E8F9 100%)",
      accent: "linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)",
    },
    shadows: {
      card: "0 2px 8px rgba(12, 74, 110, 0.06)",
      cardHover: "0 8px 24px rgba(12, 74, 110, 0.12)",
      glow: "0 4px 16px rgba(59, 130, 246, 0.3)",
      glowSecondary: "0 4px 16px rgba(6, 182, 212, 0.25)",
    },
  },
  forest: {
    id: "forest",
    name: "Forest",
    colors: {
      primary: "#14532D",
      primaryLight: "#166534",
      primaryDark: "#052E16",
      secondary: "#84CC16",
      secondaryLight: "#A3E635",
      accent: "#22C55E",
      accentLight: "#DCFCE7",
      success: "#16A34A",
      warning: "#F59E0B",
      error: "#DC2626",
      background: "#F0FDF4",
      surface: "#FFFFFF",
      surfaceAlt: "#DCFCE7",
      text: "#14532D",
      textMuted: "#4B5563",
      textInverse: "#FFFFFF",
      border: "#BBF7D0",
    },
    gradients: {
      primary: "linear-gradient(135deg, #14532D 0%, #166534 100%)",
      secondary: "linear-gradient(135deg, #84CC16 0%, #A3E635 100%)",
      accent: "linear-gradient(135deg, #22C55E 0%, #4ADE80 100%)",
    },
    shadows: {
      card: "0 2px 8px rgba(20, 83, 45, 0.06)",
      cardHover: "0 8px 24px rgba(20, 83, 45, 0.12)",
      glow: "0 4px 16px rgba(34, 197, 94, 0.3)",
      glowSecondary: "0 4px 16px rgba(132, 204, 22, 0.25)",
    },
  },
  sunset: {
    id: "sunset",
    name: "Sunset",
    colors: {
      primary: "#4C1D95",
      primaryLight: "#5B21B6",
      primaryDark: "#2E1065",
      secondary: "#F59E0B",
      secondaryLight: "#FBBF24",
      accent: "#EC4899",
      accentLight: "#FCE7F3",
      success: "#10B981",
      warning: "#F59E0B",
      error: "#EF4444",
      background: "#FAF5FF",
      surface: "#FFFFFF",
      surfaceAlt: "#F3E8FF",
      text: "#4C1D95",
      textMuted: "#6B7280",
      textInverse: "#FFFFFF",
      border: "#E9D5FF",
    },
    gradients: {
      primary: "linear-gradient(135deg, #4C1D95 0%, #5B21B6 100%)",
      secondary: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
      accent: "linear-gradient(135deg, #EC4899 0%, #F472B6 100%)",
    },
    shadows: {
      card: "0 2px 8px rgba(76, 29, 149, 0.06)",
      cardHover: "0 8px 24px rgba(76, 29, 149, 0.12)",
      glow: "0 4px 16px rgba(236, 72, 153, 0.3)",
      glowSecondary: "0 4px 16px rgba(245, 158, 11, 0.25)",
    },
  },
  midnight: {
    id: "midnight",
    name: "Midnight",
    colors: {
      primary: "#020617",
      primaryLight: "#1E293B",
      primaryDark: "#0F172A",
      secondary: "#6366F1",
      secondaryLight: "#818CF8",
      accent: "#8B5CF6",
      accentLight: "#DDD6FE",
      success: "#22C55E",
      warning: "#F59E0B",
      error: "#EF4444",
      background: "#0F172A",
      surface: "#1E293B",
      surfaceAlt: "#334155",
      text: "#F8FAFC",
      textMuted: "#94A3B8",
      textInverse: "#020617",
      border: "#334155",
    },
    gradients: {
      primary: "linear-gradient(135deg, #020617 0%, #1E293B 100%)",
      secondary: "linear-gradient(135deg, #6366F1 0%, #818CF8 100%)",
      accent: "linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)",
    },
    shadows: {
      card: "0 2px 8px rgba(0, 0, 0, 0.3)",
      cardHover: "0 8px 24px rgba(0, 0, 0, 0.4)",
      glow: "0 4px 16px rgba(139, 92, 246, 0.4)",
      glowSecondary: "0 4px 16px rgba(99, 102, 241, 0.3)",
    },
  },
};

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children, initialTheme = "navy" }) => {
  const [currentThemeId, setCurrentThemeId] = useState(initialTheme);

  const currentTheme = useMemo(() => {
    return THEMES[currentThemeId] || THEMES.navy;
  }, [currentThemeId]);

  const setTheme = useCallback((themeId) => {
    if (THEMES[themeId]) {
      setCurrentThemeId(themeId);
      // Persist to localStorage
      localStorage.setItem("studentTheme", themeId);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    const themeIds = Object.keys(THEMES);
    const currentIndex = themeIds.indexOf(currentThemeId);
    const nextIndex = (currentIndex + 1) % themeIds.length;
    setTheme(themeIds[nextIndex]);
  }, [currentThemeId, setTheme]);

  const value = useMemo(
    () => ({
      theme: currentTheme,
      themeId: currentThemeId,
      setTheme,
      toggleTheme,
      themes: THEMES,
    }),
    [currentTheme, currentThemeId, setTheme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialTheme: PropTypes.string,
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export default ThemeContext;
